'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useUserStore } from '@/hooks/useUser'
import { useSignIn, useAuthSession } from '@/hooks/useAuthActions'
import { takePostLoginRedirect } from '@/lib/post-login-redirect'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AppVersion } from '@/components/ui/app-version'
import { FcGoogle } from 'react-icons/fc'
import { AlertCircle, Loader2, Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { LOGOUT_REASON_MESSAGES } from '@/lib/auth/forced-logout'

/** Lê `?redirect=` da URL atual (sem useSearchParams, evitando exigência de Suspense). */
function readRedirectParam(): string | null {
	if (typeof window === 'undefined') return null
	return new URLSearchParams(window.location.search).get('redirect')
}

/** Lê `?reason=` — por que o middleware devolveu o usuário pra cá. */
function readReasonParam(): string | null {
	if (typeof window === 'undefined') return null
	return new URLSearchParams(window.location.search).get('reason')
}

export default function LoginPage() {
	const router = useRouter()
	const { supabaseUser } = useUserStore()
	const { signIn, isLoading } = useSignIn()
	const { isInitialized } = useAuthSession()
	const { resolvedTheme, setTheme } = useTheme()
	const [logoutReason, setLogoutReason] = useState<string | null>(null)

	// Em effect (não no render) porque `window` não existe no SSR — ler direto
	// causaria hydration mismatch.
	useEffect(() => {
		setLogoutReason(readReasonParam())
	}, [])

	useEffect(() => {
		// Com `?reason=` presente NÃO auto-redireciona: se a sessão sobreviveu por
		// algum motivo, empurrar pra home reabriria o ping-pong com o middleware e
		// a mensagem nunca apareceria.
		if (isInitialized && supabaseUser && !readReasonParam()) {
			// Já autenticado em /login: honra o ?redirect= (ou vai pra home).
			router.push(takePostLoginRedirect(readRedirectParam()))
		}
	}, [supabaseUser, isInitialized, router])

	const handleGoogleSignIn = async () => {
		try {
			await signIn(readRedirectParam() ?? undefined)
		} catch (error) {
			console.error('Login failed:', error)
		}
	}

	if (!isInitialized) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin" />
			</div>
		)
	}

	// Com `?reason=` cai no card abaixo pra mostrar a mensagem, mesmo que ainda
	// exista sessão residual no client.
	if (supabaseUser && !logoutReason) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin" />
			</div>
		)
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-background p-4">
			<Button
				variant="outline"
				size="icon"
				className="fixed right-4 top-4 z-50 border-indigo-bright/40 bg-white text-indigo-deep shadow-sm hover:bg-violet-100 hover:text-indigo-deep dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-violet-300 dark:hover:text-indigo-deep"
				onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
				aria-label={resolvedTheme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
				data-testid="login-theme-toggle"
			>
				<Moon className="h-4 w-4 dark:hidden" aria-hidden="true" />
				<Sun className="hidden h-4 w-4 dark:block" aria-hidden="true" />
			</Button>
			<Card className="w-full max-w-md" style={{ boxShadow: 'var(--shadow-lg)' }}>
				<CardHeader className="space-y-1 text-center">
					<CardTitle className="text-2xl font-bold">Bem-vindo(a) ao BX Turing</CardTitle>
					<CardDescription>Entre com sua conta do Google para acessar o painel</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{logoutReason && (
						<div
							role="alert"
							className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive dark:text-destructive-foreground"
						>
							<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
							<span>
								{LOGOUT_REASON_MESSAGES[logoutReason] ?? 'Sua sessão foi encerrada. Entre novamente.'}
							</span>
						</div>
					)}
					<Button
						onClick={handleGoogleSignIn}
						disabled={isLoading}
						variant="outline"
						className="h-12 w-full border-indigo-bright/40 bg-white text-indigo-deep shadow-sm hover:bg-violet-300 hover:text-indigo-deep hover:shadow-md dark:border-indigo-bright/40 dark:bg-white dark:text-indigo-deep dark:hover:bg-violet-300 dark:hover:text-indigo-deep"
					>
						{isLoading ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : (
							<FcGoogle className="mr-2 h-5 w-5" />
						)}
						{isLoading ? 'Entrando...' : 'Entrar com Google'}
					</Button>
				</CardContent>
			</Card>
			<AppVersion className="fixed bottom-4 left-0 right-0 text-center" />
		</div>
	)
}
